/**
 * 웹에서는 본인인증(NICE)을 새 창(팝업)으로 연다(2026-11-02 사용자 결정). 앱은 새 창을 못 띄워 같은 화면에서 한다.
 *
 * 같은 창에서 NICE 로 나갔다 오면 그 화면들이 브라우저 기록에 남아, 크롬의 뒤로 버튼이 NICE 의
 * '세션이 종료되었습니다' 로 갔다. 팝업이면 원래 창의 기록에는 NICE 가 들어오지 않는다.
 *
 * 흐름: 시작 화면(원래 창)이 팝업을 열고 결과를 기다린다(waitIdentityReturn) → 팝업이 NICE 를 거쳐
 * /auth/verify/done 으로 돌아오면 라우터가 화면을 그리기 전에 결과(state · token)를 원래 창에 넘기고
 * (handOverIdentityReturn) 스스로 닫힌다 → 원래 창이 /auth/verify/done 으로 가서 평소처럼 마무리한다.
 *
 * 넘기는 길은 둘이다 - BroadcastChannel(같은 출처의 다른 창) 과 window.opener.postMessage. NICE 가
 * Cross-Origin-Opener-Policy 를 걸면 돌아온 팝업에서 opener 가 끊길 수 있어 BroadcastChannel 을 먼저 쓴다.
 * 원래 창이 받았다고 답(ack)할 때만 팝업이 닫힌다 - 답이 없으면(같은 창 흐름) 그 창에서 그대로 마무리한다.
 */
const CHANNEL = 'aura-identity-return'
const POPUP_NAME = 'aura-identity'
const RETURN = 'identity-return'
const ACK = 'identity-return-ack'

/** 버튼을 누른 그 순간에 불러야 팝업이 막히지 않는다(await 뒤에 열면 막힌다). 막히면 null */
export function openIdentityPopup() {
  const w = 480
  const h = 720
  const left = Math.max(0, Math.round((window.screenX || 0) + ((window.outerWidth || w) - w) / 2))
  const top = Math.max(0, Math.round((window.screenY || 0) + ((window.outerHeight || h) - h) / 2))
  try {
    return window.open('', POPUP_NAME, `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`)
  } catch (_e) {
    return null
  }
}

/** /auth/verify/done 쿼리에서 넘길 값만 */
export function pickIdentityQuery(query) {
  const pick = (v) => (typeof v === 'string' ? v : '')
  return {
    state: pick(query?.state),
    identityToken: pick(query?.identityToken),
    identityError: pick(query?.identityError)
  }
}

/** 시작 화면(원래 창): 팝업이 넘겨줄 결과를 기다린다. 받으면 onReturn(query). 돌려주는 함수로 그만 듣는다 */
export function waitIdentityReturn(onReturn) {
  const handle = (data, reply) => {
    if (!data || data.type !== RETURN || !data.query || !data.query.state) return
    try {
      reply({ type: ACK, state: data.query.state })
    } catch (_e) {
      // 답을 못 보내도 결과는 받았다
    }
    onReturn(data.query)
  }
  let channel = null
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = (e) => handle(e.data, (m) => channel.postMessage(m))
  }
  const onMessage = (e) => {
    if (e.origin !== window.location.origin) return
    handle(e.data, (m) => e.source && e.source.postMessage(m, window.location.origin))
  }
  window.addEventListener('message', onMessage)
  return () => {
    if (channel) channel.close()
    window.removeEventListener('message', onMessage)
  }
}

/**
 * 완료 화면(팝업이면): 결과를 원래 창에 넘긴다. 원래 창이 받았다고 답하면 true - 이 창은 닫으면 된다.
 * 답이 없으면 false - 같은 창 흐름이니 이 창에서 마무리한다.
 */
export function handOverIdentityReturn(query, timeoutMs = 700) {
  return new Promise((resolve) => {
    let channel = null
    let timer = null
    let settled = false
    const onMessage = (e) => {
      if (e.origin === window.location.origin) onAck(e.data)
    }
    const finish = (ok) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (channel) channel.close()
      window.removeEventListener('message', onMessage)
      resolve(ok)
    }
    function onAck(data) {
      if (data && data.type === ACK && data.state === query.state) finish(true)
    }
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL)
      channel.onmessage = (e) => onAck(e.data)
    }
    window.addEventListener('message', onMessage)
    timer = setTimeout(() => finish(false), timeoutMs)

    const message = { type: RETURN, query }
    if (channel) channel.postMessage(message)
    try {
      if (window.opener && window.opener !== window) window.opener.postMessage(message, window.location.origin)
    } catch (_e) {
      // opener 가 끊겼으면 BroadcastChannel 로만 간다
    }
  })
}

/** 결과를 넘긴 팝업을 닫는다. 브라우저가 못 닫게 하면 안내만 남긴다 */
export function closeIdentityPopup() {
  try {
    window.close()
  } catch (_e) {
    // 아래 안내로 간다
  }
  setTimeout(() => {
    if (window.closed) return
    document.body.innerHTML = '<p style="margin:40px 20px;font:16px sans-serif;color:#333;text-align:center">'
      + '본인인증이 끝났습니다.<br>이 창을 닫고 원래 화면에서 이어서 진행해 주세요.</p>'
  }, 300)
}
